import React, { useMemo } from 'react';
import { useLocale, useT } from '../../contexts/LocaleProvider';
import { eventLabel } from '../../lib/holidayLabels';
import { buildDisplayNameFor } from '../../lib/eventNames';

const KIND_TONE = {
  public_holiday: { bg: 'hsl(0 60% 95%)',    fg: 'hsl(0 60% 30%)',  border: 'hsl(0 60% 80%)' },
  closure:        { bg: 'hsl(30 70% 95%)',   fg: 'hsl(30 70% 30%)', border: 'hsl(30 70% 75%)' },
  team_event:     { bg: 'hsl(150 30% 94%)',  fg: 'hsl(150 35% 25%)', border: 'hsl(150 30% 75%)' },
  absence:        { bg: 'hsl(220 20% 95%)',  fg: 'hsl(220 25% 30%)', border: 'hsl(220 20% 75%)' },
};

const WEEKDAY_KEYS = ['weekday.mon', 'weekday.tue', 'weekday.wed', 'weekday.thu', 'weekday.fri', 'weekday.sat', 'weekday.sun'];

/** Build the 6×7 grid of dates that contains a given month (Monday-first). */
function buildMonthGrid(year, monthIdx) {
  // monthIdx is 0-based (0 = Jan)
  const firstOfMonth = new Date(year, monthIdx, 1);
  // Monday-first: getDay() returns 0 (Sun) - 6 (Sat). Convert so Mon=0.
  const leading = (firstOfMonth.getDay() + 6) % 7;
  const start = new Date(year, monthIdx, 1 - leading);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const overlaps = (event, day) => {
  const di = isoDate(day);
  return event.start_date <= di && di <= event.end_date;
};

export default function MonthGrid({ year, monthIdx, events, onEventClick, onDayClick }) {
  const cells = buildMonthGrid(year, monthIdx);
  const today = isoDate(new Date());
  const t = useT();
  const { locale } = useLocale();
  const clickable = typeof onDayClick === 'function';

  const displayNameFor = useMemo(() => buildDisplayNameFor(events), [events]);

  return (
    <div className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-hidden">
      {/* Weekday header */}
      <div className="grid grid-cols-7 text-[12px] text-[hsl(var(--text-muted))] bg-[hsl(var(--bg-subtle))] border-b border-border">
        {WEEKDAY_KEYS.map(k => (
          <div key={k} className="px-3 py-2 font-medium tracking-wide uppercase">{t(k)}</div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {cells.map((day, idx) => {
          const inMonth = day.getMonth() === monthIdx;
          const di = isoDate(day);
          const isToday = di === today;
          const dayEvents = events.filter(e => overlaps(e, day));
          const visible = dayEvents.slice(0, 3);
          const overflow = dayEvents.length - visible.length;

          const cellClickable = clickable && inMonth;
          return (
            <div
              key={idx}
              onClick={cellClickable ? () => onDayClick(di) : undefined}
              role={cellClickable ? 'button' : undefined}
              tabIndex={cellClickable ? 0 : undefined}
              onKeyDown={cellClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDayClick(di); } } : undefined}
              className={[
                'border-r border-b border-[hsl(var(--rule))] min-h-[110px] p-1.5 text-[13px] flex flex-col gap-1',
                inMonth ? 'bg-[hsl(var(--surface))]' : 'bg-[hsl(var(--bg-subtle))] text-[hsl(var(--text-muted))]',
                cellClickable ? 'cursor-pointer hover:bg-[hsl(var(--surface-2))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent)/.3)] focus:ring-inset' : '',
                (idx + 1) % 7 === 0 ? 'border-r-0' : '',
                idx >= 35 ? 'border-b-0' : '',
              ].join(' ')}
            >
              <div className="flex items-center justify-between">
                <span className={`text-[12px] font-num ${isToday
                  ? 'inline-flex items-center justify-center h-5 w-5 rounded-full bg-[hsl(var(--accent))] text-white font-semibold'
                  : ''}`}>
                  {day.getDate()}
                </span>
              </div>

              <div className="flex flex-col gap-1 min-w-0">
                {visible.map(e => {
                  const tone = KIND_TONE[e.kind] || KIND_TONE.team_event;
                  const label = eventLabel(e, locale);
                  const halfTag = e.half_day === 'am' ? ' · AM' : e.half_day === 'pm' ? ' · PM' : '';
                  const who = displayNameFor(e.employee_full_name);
                  const tooltip = [
                    e.employee_full_name,
                    `${label}${halfTag}`,
                    e.description,
                  ].filter(Boolean).join('\n');
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={(ev) => { ev.stopPropagation(); onEventClick?.(e); }}
                      title={tooltip}
                      className="text-left text-[10.5px] leading-[1.15] px-1.5 py-px rounded border min-w-0 flex flex-col"
                      style={{ background: tone.bg, color: tone.fg, borderColor: tone.border }}
                    >
                      {who ? (
                        <>
                          <span className="truncate font-semibold">{who}</span>
                          <span className="truncate opacity-80">{label}{halfTag}</span>
                        </>
                      ) : (
                        <span className="truncate">{label}{halfTag}</span>
                      )}
                    </button>
                  );
                })}
                {overflow > 0 && (
                  <span className="text-[11px] text-[hsl(var(--text-muted))] px-1.5">
                    +{overflow} autre{overflow > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
